<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateBrokerCommissionBreakdown from '../components/RealEstateBrokerCommissionBreakdown.vue';
import RealEstateBrokerCommissionHero from '../components/RealEstateBrokerCommissionHero.vue';
import RealEstateBrokerInstallmentList from '../components/RealEstateBrokerInstallmentList.vue';
import { useRealEstateBrokerCommissionDetail } from '../composables/useRealEstateBrokerCommissionDetail';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const installmentId = computed(() => route.params.installmentId);
const state = useRealEstateBrokerCommissionDetail({
  installmentId,
  notify: useAlert,
  t,
});

const accountParams = computed(() => ({ accountId: route.params.accountId }));
const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor',
    params: accountParams.value,
  });
const openInstallment = id =>
  router.push({
    name: 'operacao_imobiliaria_corretor_parcela',
    params: { ...accountParams.value, installmentId: id },
  });
const openPayment = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_pagamento',
    params: { ...accountParams.value, installmentId: installmentId.value },
  });
const openRequest = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_solicitar_transferencia',
    params: { ...accountParams.value, installmentId: installmentId.value },
  });
const openDocument = () =>
  router.push({
    name: 'operacao_imobiliaria_corretor_nota',
    params: { ...accountParams.value, installmentId: installmentId.value },
  });
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <LaisHeader
      :title="t('REAL_ESTATE_BROKER_COMMISSION.TITLE')"
      :subtitle="t('REAL_ESTATE_BROKER_COMMISSION.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_BROKER_DASHBOARD.TITLE') },
        { label: t('REAL_ESTATE_BROKER_COMMISSION.TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.BACK')"
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="state.loading.value"
          @click="state.load"
        >
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="state.detail.value?.acoes?.pode_selecionar_metodo"
          icon="i-lucide-landmark"
          @click="openPayment"
        >
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.PAYMENT_DATA') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-if="state.detail.value?.acoes?.pode_solicitar_transferencia"
          icon="i-lucide-circle-check"
          @click="openRequest"
        >
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.REQUEST_TRANSFER') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          v-else-if="state.detail.value?.acoes?.pode_enviar_nota"
          icon="i-lucide-upload"
          @click="openDocument"
        >
          {{ t('REAL_ESTATE_BROKER_COMMISSION.ACTIONS.SEND_DOCUMENT') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>
    <main
      class="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
    >
      <div v-if="state.loading.value" class="space-y-4">
        <div
          v-for="index in 3"
          :key="index"
          class="h-36 animate-pulse rounded-lais bg-white"
        />
      </div>
      <p
        v-else-if="state.error.value"
        class="rounded-lais border border-red-200 bg-red-50 p-3 text-sm text-red-700"
      >
        {{ state.error.value }}
      </p>
      <template v-else-if="state.detail.value">
        <RealEstateBrokerCommissionHero :detail="state.detail.value" />
        <div
          class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.72fr)]"
        >
          <RealEstateBrokerCommissionBreakdown :detail="state.detail.value" />
          <RealEstateBrokerInstallmentList
            :installments="state.detail.value.parcelas"
            @open="openInstallment"
          />
        </div>
      </template>
    </main>
  </LaisGamificationSurface>
</template>
